/**
 * 입력창 아래 막대의 자료 종류 탭 — 회색 판 위에 고른 칸만 흰 알약이 얹힌 세그먼트 탭(10/23 지민, 참고 이미지).
 * 고른 칸만 「색 아이콘 + 짧은 이름」, 나머지는 색 없는 아이콘만 — 이름은 하나만 나와 어떤 언어로 번역해도 막대가 넘치지 않는다.
 * 고른 아이콘 색은 자료마다 다르다(주제 보라 · 파일 파랑 · 유튜브 빨강 · 웹 민트). 고르지 않은 칸 이름은 aria-label·툴팁.
 * 도구를 바꿀 때 흰 알약 하나(layoutId)가 새 칸으로 미끄러져 가고, 칸 폭·자리도 framer layout 으로 같이 옮긴다
 * (요금제 표 개인/단체 토글과 같은 방식). 떠나는 이름은 흐려지며 빠진다(popLayout). 순수 UI 다.
 */
import { Blue500, ICON_COLOR, Mint600, Red500, Violet500 } from '@zep-us/zus/tokens';
import { Tooltip } from '@zep-us/zus/tooltip';
import { AnimatePresence, LayoutGroup, motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/utils/tailwind.util';
import { AI_GENERATE_DOCK_SOURCE_TYPES, type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import { AI_GENERATE_DOCK_SOURCE_ICON } from '../consts/aiGenerateDockSourceIcon';
import { type AiGenerateDockCopy } from '../types/aiGenerateDock.types';
const SOURCE_INDICATOR_LAYOUT_ID = 'ai-generate-dock-source-indicator';

/** 고른 칸 아이콘 색 — 자료마다 다르게(10/23 지민). */
const SOURCE_SELECTED_ICON_COLOR: Record<AiGenerateDockSourceType, string> = {
  query: Violet500,
  file: Blue500,
  youtube: Red500,
  website: Mint600
};

/** 흰 알약·칸 폭 이동 — 튕김 없는 스프링 0.22s. 이름은 살짝 늦게 들어오고 빨리 빠져 겹쳐 보이지 않게 한다. */
const SWITCH_TRANSITION = {
  type: 'spring',
  bounce: 0,
  duration: 0.22
} as const;
const LABEL_ENTER_TRANSITION = {
  duration: 0.15,
  delay: 0.05
} as const;
const LABEL_EXIT_TRANSITION = {
  duration: 0.08
} as const;
const NO_MOTION = {
  duration: 0
} as const;
export const AiGenerateDockSourceSwitch = ({
  copy,
  sourceType,
  onSourceTypeChange,
  disabled
}: {
  copy: AiGenerateDockCopy;
  sourceType: AiGenerateDockSourceType;
  onSourceTypeChange: (sourceType: AiGenerateDockSourceType) => void;
  disabled?: boolean;
}) => {
  const shouldReduceMotion = useReducedMotion();
  const transition = shouldReduceMotion ? NO_MOTION : SWITCH_TRANSITION;
  return <Tooltip.Provider data-sentry-element="unknown" data-sentry-component="AiGenerateDockSourceSwitch" data-sentry-source-file="AiGenerateDockSourceSwitch.tsx">
      <LayoutGroup data-sentry-element="LayoutGroup" data-sentry-source-file="AiGenerateDockSourceSwitch.tsx">
        {/* 회색 판(높이 36 · 안쪽 2 · 모서리 10) 안에 높이 32 · 모서리 8 칸들, 칸 사이 2. */}
        <div role="group" aria-label={copy.sourceTabsLabel} className="flex shrink-0 items-center gap-4xs rounded-[10px] bg-alternative p-4xs">
          {AI_GENERATE_DOCK_SOURCE_TYPES.map(type => {
          const Icon = AI_GENERATE_DOCK_SOURCE_ICON[type];
          const selected = type === sourceType;
          return <Tooltip.Root key={type}>
                <Tooltip.Trigger asChild>
                  {/* 고르지 않은 칸은 32 정사각(아이콘만), 고른 칸은 좌우 10 + 아이콘 + 4 + 이름. */}
                  <motion.button layout transition={transition} type="button" aria-pressed={selected} aria-label={copy.sourceTabs[type]} data-testid={`ai-generate-dock-source-${type}`} disabled={disabled} onClick={() => onSourceTypeChange(type)} style={{
                borderRadius: 8
              }} className={cn('relative flex h-[32px] min-w-[32px] shrink-0 items-center justify-center whitespace-nowrap text-body-sm-semibold text-default outline-none focus-visible:ring-2 focus-visible:ring-strong disabled:opacity-50', selected && 'px-[10px]')}>
                    {selected &&
                // 흰 알약 하나가 고른 칸으로 미끄러져 간다(ZUS bg/default + shadow/xs).
                <motion.span layoutId={SOURCE_INDICATOR_LAYOUT_ID} transition={transition} aria-hidden className="pointer-events-none absolute inset-0 bg-default shadow-xs" style={{
                  borderRadius: 8
                }} />}
                    <motion.span layout="position" transition={transition} className="relative flex [&_*]:transition-[fill] [&_*]:duration-150 motion-reduce:[&_*]:transition-none">
                      {/* 아이콘은 고른 칸·나머지 모두 xs 16(10/23 지민) — 크기가 같아 바꿀 때 튀지 않는다. */}
                      <Icon size="xs" fill={selected ? SOURCE_SELECTED_ICON_COLOR[type] : ICON_COLOR.assistive} aria-hidden />
                    </motion.span>
                    <AnimatePresence initial={false} mode="popLayout">
                      {selected && <motion.span key="label" layout="position" aria-hidden initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1,
                    transition: shouldReduceMotion ? NO_MOTION : LABEL_ENTER_TRANSITION
                  }} exit={{
                    opacity: 0,
                    transition: shouldReduceMotion ? NO_MOTION : LABEL_EXIT_TRANSITION
                  }} className="relative pl-3xs">
                          {copy.sourceShortLabels[type]}
                        </motion.span>}
                    </AnimatePresence>
                  </motion.button>
                </Tooltip.Trigger>
                {!selected && <Tooltip.Content side="top" sideOffset={2} arrow>
                    {copy.sourceTabs[type]}
                  </Tooltip.Content>}
              </Tooltip.Root>;
        })}
        </div>
      </LayoutGroup>
    </Tooltip.Provider>;
};